<template>
  <div data-test-id="ChatComponent">
    <div id="chat-list" ref="chatList" class="clean-scrollbar" :class="tabWrapperClasses" data-test-id="ChatMessages">
      <div v-for="msg in messages" :key="`${msg.timestamp}-${msg.senderId}`" :class="msgClass(msg)">
        <div class="message">
          <div v-if="msg.type !== 'VOICE_MEMO'">
            <div class="message-content">{{ msg.content }}</div>
            <div class="clearfix"></div>
            <div class="message-info">
              <span>{{ getMsgFooter(msg) }}</span>
            </div>
          </div>
          <div v-else>
            <div class="message-voice-memo">
              <div class="message-length">{{ msg.length }}</div>
              <div class="message-mic">
                <b-icon icon="mic-fill" font-scale="1"></b-icon>
              </div>
            </div>
            <div class="message-info">
              <span>
                {{ new Date(msg.clientCreatedAt || msg.timestamp).toLocaleString().substring(10) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="chat-input">
      <b-form-input
        v-model="messageText"
        data-test-id="ChatInput"
        type="text"
        placeholder="Write a message"
        @keyup.enter="sendMessage"
      ></b-form-input>
      <b-icon
        v-if="messageText"
        data-test-id="ChatSendButton"
        icon="arrow-right-circle-fill"
        font-scale="2"
        @click="sendMessage"
      ></b-icon>
      <b-icon v-else icon="mic-fill" font-scale="2" data-test-id="ChatVoiceMemoButton" @click="onMicClick"></b-icon>
    </div>

    <b-modal ref="chat-media-modal" hide-footer :title="'Voice Memo'" data-test-id="chat-media-modal">
      <div class="d-block text-center message-center">
        <VoiceMemo v-if="'voicememo'" :event-type="'voicememo'" @modify-case="onModifyCase" @hide-modal="hideModal" />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { BIcon } from "bootstrap-vue";
import VoiceMemo from "@/Case/components/VoiceMemo.vue";
import { LoggerService as lgg } from "vue-components-library";
import { CaseEvent, CaseUpdateEventType } from "@/Case/CaseEvent";
import { getClientTime } from "@/Utils/getClientTime";

export default {
  name: "Chat",
  components: { BIcon, VoiceMemo },
  props: {
    messages: {
      default: () => [],
      type: Array,
    },
    tabWrapperClasses: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      messageText: "",
      connection: null,
      lastOnline: undefined,
    };
  },
  computed: {
    ...mapGetters("Case", {
      twiageCase: "twiageCase",
      tab: "tab",
      hasPatientInfo: "hasPatientInfo",
    }),
    ...mapGetters("PubNubSubscription", ["unreadChatMessages"]),
    ...mapGetters("Login", ["user"]),
    ...mapGetters("TeamSelection", ["selectedTeam"]),
    ...mapGetters("Voip", {
      isVoipCallInProgress: "isCallInProgress",
    }),
  },
  watch: {
    tab: function () {
      if (this.tab === "chat") {
        this.resetUnreadChatMessages();
      }
      this.scrollToBottom();
    },
    unreadChatMessages: function () {
      if (this.tab === "chat") {
        this.resetUnreadChatMessages();
      }
      this.scrollToBottom();
    },
    messages: function () {
      this.scrollToBottom();
    },
  },
  mounted() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      if (!navigator.onLine) {
        this.lastOnline = Date.now();
      }
      this.connection = connection;
      connection.addEventListener("change", this.updateConnectionStatus);
    }
  },
  destroyed() {
    if (this.connection) {
      this.connection.removeEventListener("change", this.updateConnectionStatus);
    }
  },
  methods: {
    ...mapActions("PubNubSubscription", ["resetUnreadChatMessages"]),
    ...mapActions("Case", ["loadChatOffline"]),
    sendMessage() {
      if (this.messageText.trim() !== "") {
        this.$emit(CaseEvent.MODIFY, {
          eventType: CaseUpdateEventType.chat,
          data: {
            content: this.messageText,
            clientCreatedAt: getClientTime(),
            timestamp: Date.now(),
            source: "web_ems",
            senderName: this.user.displayName,
            senderOrganization: this.selectedTeam.name,
            type: "CHAT",
          },
        });
      }
      this.messageText = "";
      this.scrollToBottom();
    },
    getMsgFooter(msg) {
      const formattedTime = new Date(msg.clientCreatedAt || msg.timestamp).toLocaleString("en-us").split(",")[1];
      let author = "";
      if (msg.senderName !== this.user.displayName) {
        author = `, ${msg.senderName} (${msg.senderOrganization})`;
      }
      return `${formattedTime}${author}`;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        this.$refs.chatList.scrollTop = this.$refs.chatList.scrollHeight;
      });
    },
    onMicClick() {
      this.showModal();
    },
    showModal() {
      this.$refs["chat-media-modal"].show();
    },
    hideModal() {
      this.$refs["chat-media-modal"].hide();
    },
    onModifyCase(event) {
      this.hideModal();
      this.$emit(CaseEvent.MODIFY, event);
    },
    msgClass(message) {
      return message.source;
    },
    updateConnectionStatus() {
      if (navigator.onLine) {
        this.loadChatOffline(this.lastOnline);
        this.lastOnline = undefined;

        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.ready.then((reg) => {
            if (reg) {
              reg.sync
                .register("workbox-background-sync:case")
                .catch((error) => lgg.error("[CHAT COMPONENT] Failed to fire Background Sync event", { error }));
            }
          });
        }
      } else {
        this.lastOnline = Date.now();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#chat-list {
  background-color: #222;
  height: calc(100vh - 298px);
  max-height: calc(65vh - 48px);
  overflow-y: auto;
}
#chat-list.non-twiage {
  height: calc(100vh - 374px);
  max-height: calc(65vh - 124px);
}
#chat-list.with-voip-call {
  height: calc(100vh - 374px);
  max-height: calc(65vh - 124px);
}
#chat-list.with-patient-info {
  height: calc(100vh - 341px);
  max-height: calc(65vh - 108px);
}
#chat-list.non-twiage-with-voip-call {
  height: calc(100vh - 450px);
  max-height: calc(65vh - 200px);
}
.chat-input {
  width: 100%;
  background-color: gray;
  padding: 5px;
  position: relative;
}
.chat-input input {
  width: calc(100% - 50px);
}
.chat-input .b-icon {
  position: absolute;
  right: 12px;
  top: 7px;
  cursor: pointer;
}
.message {
  display: flex;
  flex-direction: column;
  margin: 10px;

  .message-voice-memo {
    position: relative;
    height: 37px;

    .message-mic {
      background-color: #bf1311;
      border-radius: 4px;
      padding: 10px;
      margin: 0;
      z-index: 99;
      line-height: 1;
      position: absolute;
      right: 0;
    }

    .message-length {
      position: absolute;
      background-color: #fff;
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
      top: 6px;
      right: 0;
      color: #000;
      font-size: 14px;
      padding: 2px 40px 2px 6px;
      z-index: 9;
    }
  }
}
.web_ems,
.ios_ems,
.android_ems {
  display: flex;
  justify-content: flex-end;

  .message-content {
    background-color: white;
    color: black;
    border-radius: 10px;
    padding: 10px;
    margin: 0;
  }

  .message-info {
    font-size: 70%;
    margin-top: 5px;
    text-align: right;
  }
}
.ios_hospital,
.android_hospital,
.hospital_dashboard {
  display: flex;
  justify-content: flex-start;

  .message-content {
    background-color: lightblue;
    color: black;
    border-radius: 10px;
    padding: 10px;
    float: left;
    text-align: left;
  }

  .message-info {
    font-size: 70%;
    margin-top: 5px;
    text-align: left;
  }
}
</style>
